/**
 * Which response fields a caller may demand with the `requireFields` input.
 *
 * A SKU that declares `requireFields` publishes the exact list inside its input
 * schema, as the `items.enum` of that property: those are the output fields its
 * sources disagree about, so naming one picks a source that returns it. Every
 * other field is either returned by all of them or by none, and is not listed.
 *
 * The list is the input schema's, and the rows it labels are the response
 * schema's, so the two vocabularies have to be matched: a response row is a
 * dotted path (`data.posts[].isArchived`) while the enum names the field alone
 * (`isArchived`).
 */

import { parseSchema } from './schemaForm';

/** The field names this SKU's `requireFields` accepts; empty when it has none. */
export const requirableFields = (inputSchema: unknown): Set<string> => {
  const field = parseSchema(inputSchema).find((f) => f.name === 'requireFields');
  return new Set(field?.itemOptions ?? []);
};

/**
 * True when a response row names one of the requirable fields.
 *
 * Matched on the last path segment, with the array marker dropped, because the
 * enum names a field and a row names its position in the document. Nothing
 * deeper matches: `data.posts[].media[].url` is `url`, which no SKU lists, so a
 * nested member of a requirable container is not itself labelled.
 */
export const isRequirable = (rowName: string, requirable: Set<string>): boolean => {
  if (requirable.size === 0) return false;
  const path = rowName.replaceAll('[]', '');
  const leaf = path.slice(path.lastIndexOf('.') + 1);
  return leaf !== '' && requirable.has(leaf);
};
